/* eslint-disable react/no-array-index-key */
import { LogItem } from '@back-office/http';
import {
  capitalizeFLetter,
  CircleApproved,
  CircleCancelled,
  CircleRejected,
  CircleRequest,
  GcpAccordion,
  GcpBox,
  getDatetimeText,
} from '@back-office/ui';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Timeline from '@material-ui/lab/Timeline';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(
  () => ({
    timeline: {
      '& > .MuiTimelineItem-missingOppositeContent:before': {
        flex: 0,
      },
    },
    alignRight: {
      textAlign: 'right',
    },
    minHeight: {
      '&:last-child': {
        minHeight: 'auto',
      },
    },

    detailTransaction: {
      marginBottom: '2rem',
      wordBreak: 'break-all',
      '& > .detail-container': {
        margin: '-2px 0 0 6px',
        '& > span': {
          '&.requested': {
            color: '#ca8700',
          },
          '&.approved': {
            color: '#219653',
          },
          '&.rejected': {
            color: '#EB5757',
          },
          '&.cancelled': {
            color: '#BDBDBD',
          },
        },
        '& > .email': {
          color: '#828282',
        },
      },
    },
  }),
  { name: 'TransactionLogs' }
);

interface TransactionLogsProps {
  auditTrail: LogItem[];
}

const TransactionLogs: FC<TransactionLogsProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { auditTrail } = props;

  const generateDetailTransactionLog = (
    label: string,
    icon: JSX.Element,
    log: LogItem
  ): JSX.Element => (
    <GcpBox className={classes.detailTransaction} display="flex" mt={1}>
      <div>{icon}</div>
      <div className="detail-container">
        <span className={label}>
          Refund {t(`transaction_list.refund_request_status.${label}`)}
          {log.description ? ': ' : ''}{' '}
        </span>{' '}
        {log.typeReason}
        {log.typeReason ? ' | ' : ''} {log.description}
        <div className="email">
          {capitalizeFLetter(
            t(`transaction_list.refund_request_status.${label}`)
          )}{' '}
          by {log.email}
        </div>
      </div>
    </GcpBox>
  );

  const decideStatusTransactionLog = (log: LogItem): JSX.Element => {
    switch (log.label) {
      case 'refund_approved':
        return generateDetailTransactionLog(
          'approved',
          <CircleApproved />,
          log
        );
      case 'refund_requested':
        return generateDetailTransactionLog(
          'requested',
          <CircleRequest />,
          log
        );
      case 'refund_rejected':
        return generateDetailTransactionLog(
          'rejected',
          <CircleRejected />,
          log
        );
      case 'refund_cancelled':
        return generateDetailTransactionLog(
          'cancelled',
          <CircleCancelled />,
          log
        );
    }
    return <></>;
  };
  return (
    <GcpBox mb={1} mt={1}>
      <GcpAccordion
        details={
          <Timeline className={classes.timeline}>
            {auditTrail.map((item, index) => (
              <TimelineItem key={index} className={classes.minHeight}>
                <TimelineSeparator>
                  <TimelineDot color="primary" />
                  {index + 1 !== auditTrail.length && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Grid container>
                    <Grid xs={9}>
                      <GcpBox>
                        {t(`transaction_list.status.${item.label}`)}
                      </GcpBox>
                      <>{decideStatusTransactionLog(item)}</>
                    </Grid>
                    <Grid className={classes.alignRight} xs={3}>
                      {getDatetimeText(item.date as string, 'default')}
                    </Grid>
                  </Grid>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        }
        title={t('transaction_list.logs')}
      />
    </GcpBox>
  );
};

export default TransactionLogs;
